import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client';
import { useTenantHasConfluence } from '@atlassian/jira-business-confluence-pages/src/utils/use-tenant-has-confluence/index.tsx';
import {
	CARD_COVERS_TYPE,
	REMOTE_ISSUE_LINKS_TYPE,
} from '@atlassian/jira-business-constants/src/index.tsx';
import { shouldHandleTheError } from '@atlassian/jira-business-error-handling/src/utils/partial-data/index.tsx';
import {
	SUBTASKS_TYPE,
	ISSUE_LINKS_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type {
	/* eslint-disable-next-line camelcase */
	JIS2JWMBoardItems_jira_jwmViewItems_JiraWorkManagementViewItemConnection_edges_node_fieldsByIdOrAlias,
	JIS2JWMBoardItems,
	/* eslint-disable-next-line camelcase */
	JIS2JWMBoardItems_jira_jwmViewItems_JiraWorkManagementViewItemConnection_edges_node,
	JIS2JWMBoardItemsVariables,
} from './__generated_apollo__/JIS2JWMBoardItems';
import { JIS2_JWMBoardItemsQuery } from './gql.tsx';

const ITEMS_PER_PAGE = 100;

export type IssueDetailsJIS2 =
	/* eslint-disable-next-line camelcase */
	JIS2JWMBoardItems_jira_jwmViewItems_JiraWorkManagementViewItemConnection_edges_node;

export type IssueDetailFieldJIS2 =
	/* eslint-disable-next-line camelcase */
	JIS2JWMBoardItems_jira_jwmViewItems_JiraWorkManagementViewItemConnection_edges_node_fieldsByIdOrAlias;

type FetchArguments = {
	jql: string;
	fieldIds: string[];
};

export const useFetchBoardIssues = () => {
	const client = useApolloClient();
	const cloudId = useCloudId();
	const hasConfluence = useTenantHasConfluence();

	return useCallback(
		async ({ jql, fieldIds: fieldIdsInput }: FetchArguments): Promise<IssueDetailsJIS2[]> => {
			const fieldIds = new Set(fieldIdsInput);
			const withChildrenCount = fieldIds.has(SUBTASKS_TYPE);
			fieldIds.delete(SUBTASKS_TYPE);
			const withConfluenceLinks = hasConfluence && fieldIds.has(REMOTE_ISSUE_LINKS_TYPE);
			fieldIds.delete(REMOTE_ISSUE_LINKS_TYPE);
			const withIssueLinks = fieldIds.has(ISSUE_LINKS_TYPE);
			fieldIds.delete(ISSUE_LINKS_TYPE);
			const withCoverMedia = fieldIds.has(CARD_COVERS_TYPE);
			fieldIds.delete(CARD_COVERS_TYPE);

			const variables: JIS2JWMBoardItemsVariables = {
				fieldIds: Array.from(fieldIds),
				first: ITEMS_PER_PAGE,
				jql,
				withConfluenceLinks,
				withCoverMedia,
				cloudId,
				withIssueLinks,
				withChildrenCount,
			};

			const response = await client.query<JIS2JWMBoardItems, JIS2JWMBoardItemsVariables>({
				query: JIS2_JWMBoardItemsQuery,
				fetchPolicy: 'no-cache',
				errorPolicy: 'all',
				variables,
			});

			if (!response) {
				throw new Error('No issue response');
			}

			if (response.errors) {
				const apolloError = new ApolloError({
					graphQLErrors: response.errors,
				});

				if (shouldHandleTheError(apolloError)) {
					throw apolloError;
				}
			}

			const responseData = response.data.jira?.jwmViewItems;

			if (
				responseData?.__typename === 'JiraWorkManagementViewItemConnection' &&
				responseData.edges != null
			) {
				return responseData.edges
					.map((edge) => edge?.node)
					.filter((node): node is IssueDetailsJIS2 => node != null);
			}

			throw new Error('Invalid issue response');
		},
		[client, cloudId, hasConfluence],
	);
};
